// Formatting and roster rules shared by the provider portal and My care team.

export const INACTIVE_DAYS = 7;
export const CODE_LENGTH = 6;

const DAY_MS = 24 * 60 * 60 * 1000;

// "4 Apr 2026"
export const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// "4 April 2026"
export const formatLongDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

// "March 2026"
export const formatMonthYear = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : "";

// "Tue 20 Oct, 10:30"
export const formatWhen = (iso) => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) +
    ", " +
    d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
  );
};

// Week starts come from the API as "2026-08-17" → "Aug 17".
export const weekLabel = (isoDay) => {
  const [y, m, d] = isoDay.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

export const lastActiveLabel = (days) => {
  if (days === null || days === undefined) return "No sessions yet";
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

export const daysUntil = (iso, now = Date.now()) =>
  Math.max(0, Math.ceil((new Date(iso).getTime() - now) / DAY_MS));

// The sub-line under a pending roster row.
export const pendingNote = (row, now = Date.now()) => {
  if (row.kind === "invite") {
    if (row.expired) return `Invite code ${row.code} · expired`;
    const days = daysUntil(row.expires_at, now);
    return `Invite code ${row.code} · expires in ${days} day${days === 1 ? "" : "s"}`;
  }
  return "Account created · waiting for first sign-in";
};

// A pending row is an invite, or (from before invites replaced it) an
// account the provider created that waits for the patient's approval.
export const cancelLabel = (row) =>
  row.kind === "invite" ? "Cancel invite" : "Withdraw request";

export const isFlagged = (row) =>
  row.status === "inactive" ||
  (row.days_since_active !== null &&
    row.days_since_active !== undefined &&
    row.days_since_active >= INACTIVE_DAYS);

export const rosterSummary = (rows) => {
  const n = rows.length;
  if (!n) return "";
  const inactive = rows.filter((r) => r.status === "inactive").length;
  const pending = rows.filter((r) => r.status === "pending").length;
  let text = `${n} patient${n > 1 ? "s" : ""}`;
  if (inactive) text += ` · ${inactive} with no activity in 7+ days`;
  if (pending) text += ` · ${pending} pending`;
  return text;
};

export const filterRoster = (rows, query, filter) => {
  const q = query.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!q || r.name.toLowerCase().includes(q)) &&
      (filter === "all" || r.status === filter),
  );
};

// "Dr. Hannah Moss" → "Dr. Moss"; anyone else keeps their full name.
export const shortName = (name) => {
  const words = (name || "").trim().split(/\s+/);
  if (/^dr\.?$/i.test(words[0]) && words.length > 2) {
    return `${words[0]} ${words[words.length - 1]}`;
  }
  return name;
};

export const initials = (name) =>
  (name || "")
    .replace(/^dr\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("") || "?";

export const providerLine = (provider) =>
  [provider.profession_label, provider.organization].filter(Boolean).join(", ");

// Codes are typed loosely: drop anything that isn't a letter or digit.
export const sanitizeCode = (value) =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, CODE_LENGTH);

export const CODE_ERRORS = {
  format:
    "A provider code has exactly 6 letters or numbers. Please check it and try again.",
  not_found:
    "We couldn't find that code. Check each letter and number, or ask your provider to send it again.",
  expired:
    "This code has expired. Please ask your provider for a new one.",
};

export const codeErrorText = (error) => {
  if (!error) return "";
  if (error.error === "already_connected") {
    return `You're already sharing with ${error.provider_name}. There's nothing more you need to do.`;
  }
  return CODE_ERRORS[error.error] || CODE_ERRORS.not_found;
};

// Round an axis maximum up to a tidy number (1, 2, 2.5, 5 × 10ⁿ).
export const niceMax = (value) => {
  if (!value || value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const steps = [1, 2, 2.5, 5, 10];
  const step = steps.find((s) => s * exp >= value);
  return step * exp;
};

export const formatScore = (score) =>
  score === null || score === undefined
    ? "—"
    : Number.isInteger(score)
      ? String(score)
      : String(Math.round(score * 10) / 10);

// Whole seconds of play → "45 sec", "4 min 0 sec", "1 hr 0 min 5 sec". Only
// leading zero units are left out.
export const formatPlayTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours) return `${hours} hr ${minutes} min ${secs} sec`;
  if (minutes) return `${minutes} min ${secs} sec`;
  return `${secs} sec`;
};

export const PROFESSIONS = [
  ["occupational_therapist", "Occupational therapist"],
  ["speech_language_therapist", "Speech-language therapist"],
  ["neuropsychologist", "Neuropsychologist"],
  ["physician", "Physician"],
  ["nurse", "Nurse"],
  ["other", "Other"],
];

// Pull a readable message out of a DRF error response.
export const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  // A proxy or 404 page comes back as HTML text, not a DRF error object.
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string") return data.detail;
  for (const value of Object.values(data)) {
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    if (typeof value === "string") return value;
  }
  return fallback;
};
